import { IPortalDynamicShippingOptionDto } from "../../../services/api/create-rma-service";
import { RMA_STATUS_TRANSLATION_KEY } from "../../../services/api/rma-list-service";
import { RESOLUTION_ID } from "../../resolutions/constants";
import { FEE_TYPE, IPolicyFee, IPolicyRule } from "../../policies/policy-types";
import { CP_DIMENSION_UNIT, IAggregatorInfo, IReturnMethod, RETURN_METHOD_TYPE, WEIGHT_UNIT } from "../../return-methods/return-method-types";
import {IManagerCredentials} from "../../../manager-approval-modal/manager-credentials-stores";
import { INCENTIVE_VIRTUAL_CURRENCIES } from '../../../models/User';
import { IResolution } from "../../../models/ResolutionTypes";
import { SHIPPING_CONNECTION_CODE } from "../../../models/IntegrationsTypes";


export interface IRmaPolicyRule extends Pick<IPolicyRule,
    "id" |
    "name" |
    "charge_fees"> {
    policy_rule_fees: IRmaPolicyRuleFee[];
    merchant_resolution_setting: IRmaResolution;
}

export interface IRmaPolicyRuleFee extends Partial<IPolicyFee> {
    id: number;
    is_charged: boolean;
    /* Please note this type is not transferred AS IS from IPolicyFee - there it's FEE_TYPE | null :O */
    fee_type: FEE_TYPE;
}

export interface IRmaResolution extends Partial<IResolution> {
    id: number;
    internal_name: string;
    resolution_id: RESOLUTION_ID;
}

export interface IRmaReturnReason {
    id: number;
    _internal_name: string;
}
export interface IRmaSettings {
    return_methods: IRMAReturnMethodPreset[];
    store_locations: IStoreLocationsMap;
    has_restock_permission: boolean;
    default_refund_level: REFUND_LEVEL;
    aggregators_info: IAggregatorInfo[];
    store_currency: string;
}

export enum REFUND_LEVEL {
    LINE_ITEM_REFUND_LEVEL = 'LineItem',
    ORDER_REFUND_LEVEL = 'Order',
}

export interface IRMAReturnMethodPreset {
    id: number;
    internal_name: string;
    type: RETURN_METHOD_TYPE;
}

interface IStoreLocationsMap {
    [location_id: number]: IStoreLocation;
}

export interface IStoreLocation {
    name: string;
    location_id: string;
    fulfillsOnlineOrders: boolean;
    /** Can be extended on demand, since each location has more fields */
}

export interface IRMAEmailLog {
    id: number;
    label: string;
    type: string;
    translation_key: string;
}

export interface IRmaProduct {
    id: number;
    image: string;
    product_name: string;
    variant_name: string | null;
    sku: string | null;
    price: string;
    balance: string;
}
export interface IUploadedMediaFile {
    is_saved: boolean;
    is_image: boolean;
    is_video: boolean;
    absolute_url: string;
}

export interface IRmaProductCase {
    restocked_comment: string | null;
    returned_comment: string | null;
    id: number;
    is_rejected: boolean;
    rejected_reason: string | null;
    product: IRmaProduct;
    merchant_return_reason: IRmaReturnReason;
    product_case_offers: IProductCaseOffer[];
    paid_price: number;
    questions: IProductCaseQuestion[];
    exchange: IExchange;
    answers: string | null;
    _uploaded_media_files: IUploadedMediaFile[];
    is_returned: boolean | null;
    is_restockable: boolean | null;
    is_restocked: boolean | null;
    return_method: IReturnMethod;
    shipment_id?: number;
    _credited_amount: string;
    _automatically_credited_amount: string;
    _automatically_credited: boolean;
    _gifted_amount: string;
    _automatically_gifted_amount: string;
    _automatically_gifted: boolean;
    _is_refunded: boolean;
    _is_successfully_refunded: boolean;
    _refunded_amount: number;
    _bank_refunded_amount: number;
    _external_refund_amount: number;
    _platform_store_credit_refunded_amount: number;
    _is_automatically_refunded: boolean;
    // In addition to "is_restocked" Model field, we maintain computed property _is_restocked which
    // is computed on the backend when  we send request to the /rma/{id} API endpoint
    _is_restocked: boolean | null;
    // In addition to "is_restockable" Model field, we maintain computed property _is_restockable which
    // is computed on the backend when  we send request to the /rma/{id} API endpoint
    _is_restockable: boolean;
    _restocked_platforms: string | null;
    _bxgy_discount_data: IBxGyDiscountData | null;
    _cap_discount_data: ICapDiscountData | null;
    _items_balance: IItemBalanceBreakdownDto | null;
    validation_images: IValidateItemImage[];
    _is_hidden_resolution: boolean;
    has_incentive_from_switch_to_exchange: boolean;
    _can_reject: ICanReject
}

export interface ICanReject {
    reason: CANT_REJECT_ITEM_REASONS | null;
    result: boolean;
}

export enum CANT_REJECT_ITEM_REASONS {
    DRAFT_FEE_ORDER_IS_CREATED_AND_IN_STATUS_PAID = 'DRAFT_FEE_ORDER_IS_CREATED_AND_IN_STATUS_PAID',
    INCREASED_AMOUNT_TO_PAY_VIA_PORTAL_CHECKOUT = 'INCREASED_AMOUNT_TO_PAY_VIA_PORTAL_CHECKOUT',
    LAST_ITEM_WITH_EXCHANGE_RESOLUTION_TYPE = 'LAST_ITEM_WITH_EXCHANGE_RESOLUTION_TYPE',
    ITEM_IS_ALREADY_REJECTED = 'ITEM_IS_ALREADY_REJECTED',
    RMA_IS_NOT_IN_PENDING_APPROVAL = 'RMA_IS_NOT_IN_PENDING_APPROVAL',
    FEATURE_IS_NOT_SUPPORTED = 'FEATURE_IS_NOT_SUPPORTED',
    CAN_NOT_REJECT_LAST_ITEM = 'CAN_NOT_REJECT_LAST_ITEM',
    LAST_ITEM_WITH_SHIPPING_REQUIRED_METHOD = 'LAST_ITEM_WITH_SHIPPING_REQUIRED_METHOD',
}

export interface IBxGyDiscountData {
    discount_code: string;
    is_buy: boolean;
    is_get: boolean;
}

export interface ICapDiscountData {
    discount_amount: number;
    discount_percentage: number;
}
export interface IMoneyDto {
    amount: number,
    currency_code: string,
}

interface IDeductionDto {
    restocking_fee: IMoneyDto[],
    return_label_fee: IMoneyDto[],
    rma_fee: IMoneyDto[],
    cancellation_fee: IMoneyDto[],
    order_shipping_cost: IMoneyDto[],
    price_difference: IMoneyDto[],
}

export interface IOtherItemsPaymentsDto {
    product_case_id: number,
    total_amount: IMoneyDto[],
}

interface IPaymentsDto {
    is_paid: boolean,
    restocking_fee: IMoneyDto[],
    return_label_fee: IMoneyDto[],
    rma_fee: IMoneyDto[],
    cancellation_fee: IMoneyDto[],
    price_difference: IMoneyDto[],
}

export enum ORIGINAL_ITEM_PRICE_SOURCE {
    PAID_PRICE = 'paid_price',
    CATALOG_PRICE = 'catalog_price',
    CATALOG_PRICE_TIME_OF_PURCHASE = 'catalog_price_time_of_purchase',
}

export const ORIGINAL_ITEM_PRICE_SOURCE_TRANSLATIONS_MAP = {
    [ORIGINAL_ITEM_PRICE_SOURCE.PAID_PRICE]: 'PAID_PRICE' ,
    [ORIGINAL_ITEM_PRICE_SOURCE.CATALOG_PRICE]: 'CATALOG_PRICE',
    [ORIGINAL_ITEM_PRICE_SOURCE.CATALOG_PRICE_TIME_OF_PURCHASE]: 'CATALOG_PRICE_TIME_OF_PURCHASE',
}

export enum NEW_ITEM_PRICE_SOURCE {
    OVERRIDE_THE_ORIGINAL_REQUEST = 'OVERRIDE_THE_ORIGINAL_REQUEST',
    FOLLOW_THE_ORIGINAL_REQUEST = 'FOLLOW_THE_ORIGINAL_REQUEST',
}

export const NEW_ITEM_PRICE_SOURCE_TRANSLATIONS_MAP = {
    [NEW_ITEM_PRICE_SOURCE.OVERRIDE_THE_ORIGINAL_REQUEST]: 'OVERRIDE_THE_ORIGINAL_REQUEST',
    [NEW_ITEM_PRICE_SOURCE.FOLLOW_THE_ORIGINAL_REQUEST]: 'FOLLOW_THE_ORIGINAL_REQUEST',
}

export interface INewItemDiscount {
	code: string;
	type: 'in-store' | 'bxgy-adjustment'
}

export interface IItemBalanceBreakdownDto {
    balance: IMoneyDto[],
    original_item_paid_price: IMoneyDto[],
    original_item_price_for_exchange: IMoneyDto[],
    original_item_price_source: ORIGINAL_ITEM_PRICE_SOURCE | null,
    price_difference_adjustments: IMoneyDto[],
    new_item_price: IMoneyDto[],
    new_item_price_source: NEW_ITEM_PRICE_SOURCE | null,
    new_item_price_difference_adjustments: IMoneyDto[],
    new_item_bxgy_discount_adjustment: IMoneyDto[],
    new_item_applied_discount: IMoneyDto[],
    new_item_discounts: string[] | INewItemDiscount[],
    price_difference: IMoneyDto[],
    exchange_range_adjustment: IMoneyDto[],
    switch_to_exchange_incentive: IMoneyDto[],
    refund_adjustments: IMoneyDto[],
    compare_at_price_discount: IMoneyDto[],
    compare_at_price_discount_percentage: number | null,
    deduction: IDeductionDto,
    pending_payments: IPaymentsDto,
    paid_by_other_items: IOtherItemsPaymentsDto[],
    paid_for_other_items: IOtherItemsPaymentsDto[],
    discount_fee: IMoneyDto[],
    restocking_fee: IMoneyDto[],
    return_label_fee: IMoneyDto[],
    rma_fee: IMoneyDto[],
    cancellation_fee: IMoneyDto[],
    original_item_catalog_price_at_time_of_purchase: IMoneyDto[],
    original_item_compare_at_price: IMoneyDto[],
    applied_bxgy_discount: IMoneyDto[],
    applied_basic_discount: IMoneyDto[],
    applied_custom_discount: IMoneyDto[],
}

export interface IValidateItemImage{
    _full_url: string;
    id: number;
    filename: string;
    is_saved: boolean;
    _is_deleted?: boolean;
}

export interface IProductCaseOffer {
    credits: number,
    switch_to_exchange_incentive: number|null,
    incentive_virtual_currency: INCENTIVE_VIRTUAL_CURRENCIES | null,
    return_label_fee: number|null,
    fee: number|null,
    status: PRODUCT_CASE_OFFER_STATUS,
    policy_rule: IRmaPolicyRule;
}
export interface IExchange {
    id: number;
    image: string;
    new_item_product_name: string;
    new_item_variant_title: string;
    new_item_image_url: string | null;
    sku: string | null;
    quantity: number;
    new_item_catalog_price_time_of_request: number;
    new_item_catalog_price_time_of_draft_order: number;
    new_item_catalog_price_time_of_draft_order_release: number;
    new_item_catalog_price_currency_code: string | null;
    new_item_contextual_price_time_of_request: number;
    new_item_contextual_price_time_of_draft_order: number | null;
    new_item_contextual_price_time_of_draft_order_release: number | null;
    new_item_contextual_price_currency_code: string | null;
    new_item_adjusted_price: number | null;
    new_item_adjusted_price_currency: string | null;
    variant_id: string;
    product_case_id: number | null;
    discounts: string[] | INewItemDiscount[] | null;
    _applied_discount: IMoneyDto[] | null;
    _bxgy_discount_adjustment: IMoneyDto[] | null;
}
export interface IProductCaseQuestion{
    id: number;
    translation: string;
    answers: IProductCaseAnswer[];
    images: IRMAQuestionMedia[];
}

export interface IRMAQuestionMedia {
    _full_url: string;
    id: number;
    filename: string;
    is_saved: boolean;
}

export interface IProductCaseAnswer {
    id: number;
    answer: string;
    translation: string;
}

export interface IRmaShipment {
	id: number;
	status: SHIPMENT_STATUS_ENUM;
    tracking_number: string | null;
    tracking_url: string | null;
    return_label_url: string | null;
    qr_image_url: string | null;
    qr_request_json: string | null;
    packing_slip_url: string | null;
    carrier: string | null;
    is_outbound_shipment: boolean;
    _service_level: string | null;
    _aggregator_code: SHIPPING_CONNECTION_CODE | null;
}

export interface IRmaCustomerCase {
	id: number;
    locale: string;
    accepted_product_cases: IRmaProductCase[];
    payment_currency_code: string;
    order_name:string;
    glbe_order_id: string|null;
    customer_id: string;
    conversion_rate: number;
    return_label_fee: number|null;
    rma_fee: number|null;
    rma_score: string|null;
    rma_score_reason: string|null;
    cancellation_fee: number|null;
    payments: IRmaPyment[];
    exchange_orders: IRmaExchangeOrder[];
    is_gift: boolean;
    address_id: string|null,
    location_id: string|null,
    _reserve_item_count: number;
    _reserve_until: string|null;
    _number_of_items_message: string;
    _should_create_fee_draft_order: boolean;
    _should_return_label_fee_be_always_charged: boolean;
    _total_product_cases_count: number;
    _updated_shipping_address?: string;
    exchanges: IExchange[];
    used_coverage: boolean;
}
export interface IRmaPyment {
    payment_breakdown: string;
    status: PAYMENT_STATUS;
    payment_id: string;
    _full_amount?: number;
    _discounted_amount?: number;
    _currency?: string;
    _authorization_expiration_days_left?: number;
    _authorized_date?: string;
    _authorization_expiration_date?: string;
    _capture_date?: string;
    _cancel_date?: string;
    _captured_amount?: number;
    _is_hidden: boolean;
}
export interface IRmaExchangeOrder{
    _order_link: string | null;
    _draft_order_link: string;
    release_date: string|null;
    reserve_until: string;
    draft_fee_order_id: string | null;
    order_name: string;
    order_id: string | null;
    draft_order_id: string;
    draft_order_type: DRAFT_ORDER_TYPE;
    internal_state: DRAFT_ORDER_STATE;
    total_amount_at_the_time_of_draft_order_creation: number;
}

/** Caption and body of the error Note shown for a timeline error. */
export interface IErrorNoteContent {
    caption: string;
    body: string;
}

/** The carrier's AddressDto, sent as it is; both addresses carry the same fields. */
export interface IShippingDetailsAddress {
    name: string | null;
    email: string | null;
    company: string | null;
    street1: string | null;
    street2: string | null;
    city: string | null;
    state: string | null;
    state_name: string | null;
    country: string | null;
    zip: string | null;
    phone: string | null;
    suburb: string | null;
}

/**
 * The carrier's ParcelDto as the shipment would send it. The weight is restated in the return
 * method's weight unit, the dimensions stay in the method's own. The wire keys are camelCase.
 */
export interface IShippingDetailsParcel {
    weight: number;
    weightUnits: WEIGHT_UNIT;
    length: number | null;
    width: number | null;
    height: number | null;
    distanceUnits: CP_DIMENSION_UNIT | null;
}

/**
 * The parcel as it was read, as the card holds it and the quote and the approve take it back. The
 * weight is null only while the merchant has cleared it, which the card refuses, so a parcel that is
 * sent always carries one; the unit is null only in a store made before anything was fetched, which
 * has no card to send.
 */
export type IHeldShippingDetailsParcel = Omit<IShippingDetailsParcel, "weight" | "weightUnits"> & {
    weight: number | null;
    weightUnits: WEIGHT_UNIT | null;
};

/** ProductItemDto::RETURN_TYPE_* of the carrier API. */
export enum SHIPPING_DETAILS_ITEM_RETURN_TYPE {
    RETURN = 'RETURN',
    EXCHANGE = 'EXCHANGE',
}

/** The carrier's Money. */
export interface IShippingDetailsMoney {
    amount: number;
    currency: string;
}

/** The carrier's ProductPriceDetailsDto. */
export interface IShippingDetailsItemPriceDetails {
    paidPriceMoney?: IShippingDetailsMoney[] | null;
    catalogPriceMoney?: IShippingDetailsMoney[] | null;
    costPriceMoney?: IShippingDetailsMoney[] | null;
}

/**
 * The carrier's ProductItemDto, one per returned line, keys exactly as on the wire: mostly
 * camelCase, product_case_id snake_case. Only sku, qty and product_case_id are always there.
 * The rest is filled only when the aggregator takes per-item info (item_weights_required) and is
 * otherwise left out of the JSON altogether, the weight included.
 */
export interface IShippingDetailsItem {
    sku: string;
    qty: number;
    /** The product case id as a numeric string. */
    product_case_id: string;
    description?: string | null;
    weight?: number | null;
    /** Restated in the return method's weight unit, like the parcel. */
    weightUnit?: WEIGHT_UNIT | null;
    paidPrice?: number | null;
    catalogPrice?: number | null;
    catalogCurrency?: string | null;
    catalogPriceInUsd?: number | null;
    hsCode?: string | null;
    returnReason?: string | null;
    globalEReturnReasonCode?: string | null;
    globalEReturnReasonId?: string | null;
    /** One entry per line, null when the line has no image. */
    images?: (string | null)[] | null;
    productName?: string | null;
    barcode?: string | null;
    productId?: string | null;
    returnType?: SHIPPING_DETAILS_ITEM_RETURN_TYPE | null;
    priceDetails?: IShippingDetailsItemPriceDetails | null;
    countryCodeOfOrigin?: string | null;
    lineItemId?: string | null;
    productType?: string | null;
}

export interface IShippingDetailsCountry {
    name: string;
    code: string;
}

/**
 * Every visible return method of the shop, for the change shipping option path, including the
 * ones a carrier cannot quote: supports_shipping_rates tells them apart.
 */
export interface IShippingDetailsReturnMethod {
    id: number;
    name: string;
    type: RETURN_METHOD_TYPE;
    internal_name: string;
    /**
     * True when a carrier quotes the method: it is approved on a rate the merchant picks. False
     * when none does: it is approved on its own settings, and asking for its rates would move the
     * RMA onto it and then fail.
     */
    supports_shipping_rates: boolean;
}

/**
 * Response of GET /manage-returns/rma/{id}/fetch-shipping-details. One call feeds every card of
 * the resolve-label-error flow, so the merchant never waits per section. The addresses, the parcel
 * and the items are read off the request the shipment would send to the carrier.
 */
export interface IFetchShippingDetailsResponse {
    customer_address: IShippingDetailsAddress;
    return_address: IShippingDetailsAddress;
    parcel: IShippingDetailsParcel;
    items: IShippingDetailsItem[];
    /** False when the aggregator takes no per-item weights; an item's weight may then be missing. */
    item_weights_required: boolean;
    countries: IShippingDetailsCountry[];
    /**
     * The RMA's own method, answered whatever its visibility: return_methods leaves out one hidden
     * after the RMA was placed on it.
     */
    current_return_method: IShippingDetailsReturnMethod;
    return_methods: IShippingDetailsReturnMethod[];
}

/** A rate as the portal quotes it; the same shape the customer-facing flow already reads. */
export type { IPortalDynamicShippingOptionDto };

/**
 * What the merchant edited, sent both to re-quote the rates and to approve on them
 * (CorrectedShipmentDetailsDto::fromRequest): the prefetch answer with the merchant's edits, in the
 * prefetch's own keys, which the backend reads back with the serializer that wrote them. All four
 * sections go every time, whether or not the merchant opened their card: an unopened card goes back
 * exactly as it was read, and a body missing one is refused.
 */
export interface IFetchShippingRatesRequest {
    customer_address: IShippingDetailsAddress;
    return_address: IShippingDetailsAddress;
    parcel: IHeldShippingDetailsParcel;
    /** Every line with every field it was read with, weighed or not. */
    items: IShippingDetailsItem[];
}

/**
 * The approve that regenerates the label from the edited details, on a method no carrier quotes
 * (supports_shipping_rates false): the four sections as the merchant left them
 * (CorrectedShipmentDetailsDto::fromRequestIfPresent - all four or none), and the label is bought on
 * the method's own settings. The parcel is not saved back to the return method, so there is nothing
 * to ask for it.
 */
export interface IResolveLabelErrorUnquotedApproveRequest extends IFetchShippingRatesRequest {
    save_return_address: boolean;
    send_email_notification: boolean;
}

/** The same approve on a quoted method: the four sections the quote was made on, plus the rate picked from it. */
export interface IResolveLabelErrorQuotedApproveRequest extends IResolveLabelErrorUnquotedApproveRequest {
    selected_rate_id: number;
}

/**
 * What the validate shipment details path approves on. A union for the same reason as the change
 * shipping option one: the method decides whether a rate goes, and a quoted method cannot lose it.
 */
export type IResolveLabelErrorApproveRequest =
    | IResolveLabelErrorQuotedApproveRequest
    | IResolveLabelErrorUnquotedApproveRequest;

/**
 * The approve of a method a carrier quotes: the RMA moves onto it and the label is bought on the
 * rate the merchant picked. The label is regenerated from that method's own defaults - there is
 * nothing of the merchant's to override on this path, so none of the four correction sections go.
 */
export interface IChangeShippingOptionQuotedApproveRequest {
    new_return_method_id: number;
    selected_rate_id: number;
    send_email_notification: boolean;
}

/**
 * The approve of a method no carrier quotes (supports_shipping_rates false): there is no rate to
 * pick, so the RMA moves onto the method and is approved on that method's own settings.
 */
export interface IChangeShippingOptionUnquotedApproveRequest {
    new_return_method_id: number;
    send_email_notification: boolean;
}

/**
 * What the change shipping option path approves on. A union and not an optional rate: whether a
 * rate goes is decided by the method, and each branch of the store is held to its own shape, so a
 * quoted method cannot lose its rate on the way to the backend.
 */
export type IChangeShippingOptionApproveRequest =
    | IChangeShippingOptionQuotedApproveRequest
    | IChangeShippingOptionUnquotedApproveRequest;

export interface IRmaError {
    id: number;
    short_error: string | null;
    is_resolved: boolean;
    /** Set on timeline rows only: this error opens the edit shipment details flow. */
    is_resolve_label_error?: boolean;
    vendor_label?: string | null;
    classified_errors?: IClassifiedError[] | null;
}

interface IGlobalERma {
    return_id: string | null;
    order_id: string | null;
}

/** Present on fetchRma when this return is still held by an unfinished bulk action. */
export interface IRmaBulkRmaAction {
    action: 'mark_done';
    request_status: string;
}

export interface IRma {
    id: number;
    created_at: string;
    customer_case: IRmaCustomerCase;
    return_errors: IRmaError[];
    shipments: IRmaShipment[];
    status: RMA_STATUS;
    is_restock_validated: boolean;
    _status: RMA_STATUS_TRANSLATION_KEY;
    _overall_shipment_status: SHIPMENT_STATUS_ENUM|null;
    _rma_return_method: IReturnMethod;
    _email_logs: IRMAEmailLog[];
    timeline: ITimelineItemDto[];
    required_manager_approval: boolean;
    required_manager_approval_reason?: string;
    is_out_of_policy: boolean;
    /** Present when Global-E RMA payload capability is enabled for the merchant; omitted or null otherwise */
    global_e_rma?: IGlobalERma | null;
    _bulk_rma_action?: IRmaBulkRmaAction | null;
}

/**
 * Generic common RMA action store.
 * Every RMA action exposes submit + loading state.
 */
export interface IRmaActionSubmitStore {
    get isProcessing(): boolean;
    submitAction(closeCb?: () => void): Promise<void>;
}

/** Stores used by email-notification modals (approve, receive, reopen, cancel, reject flows). */
export interface IRmaActionEmailNotificationStore extends IRmaActionSubmitStore {
    get sendEmailNotification(): boolean;
    setSendEmailNotification(v: boolean): void;
    get emailAdditionalContent(): string | undefined;
    setEmailAdditionalContent(v: string): void;
    get emailAttachments(): IEmailAttachment[];
    addEmailAttachment(attachment: IEmailAttachment): void;
    removeEmailAttachment(attachment: IEmailAttachment): void;
    hasCustomerEmail(): boolean;
}

/** Optional reason row (cancel & reject flow). */
export interface IRmaActionReasonFieldStore {
    get reasonField(): IActionReasonField | undefined;
}

/** Return method change store (approve flow). */
export interface IRmaApproveReturnMethodChangeStore {
    get newReturnMethodId(): number | undefined;
    setNewReturnMethodId(v: number): void;
    get returnMethodWarning(): string | null;
    get showReturnMethodChangeWarning(): boolean;
    resetReturnMethodChange(): void;
    confirmReturnMethodsChangeWithLabelFees(): void;
    confirmReturnMethodsChangeWithoutLabelFees(): void;
}

/** Restock modal store (no email fields). */
export interface IRmaRestockActionStore extends IRmaActionSubmitStore {
    get restockingLocationId(): string | undefined;
    setRestockingLocationId(v: string): void;
    setSelectedProductCaseIds(v: number[]): void;
}

export interface IActionReasonField {
    label: string,
    value: string | undefined,
    onChange: (v: string) => void
}

export interface IRmaApproveRequest {
    send_email_notification: boolean;
    email_attachment: IEmailAttachment[];
    email_additional_content?: string;
    new_return_method_id?: number;
    cancel_return_label_fee: boolean;
    manager_approval_credentials?: IManagerCredentials;
}

export interface IReceiveShipmentRequest {
    send_email_notification: boolean;
    email_attachment: IEmailAttachment[];
    email_additional_content?: string;
}

export interface IRmaRejectRequest {
    send_email_notification: boolean;
    email_attachment: IEmailAttachment[];
    email_additional_content?: string;
    reject_reason: string;
}

export interface IRmaRejectItemRequest {
    product_case_id: number
    reject_reason: string|null
}

export interface IRmaReopenReuest {
    send_email_notification: boolean;
    email_attachment: IEmailAttachment[];
    email_additional_content?: string;
}

export interface IRmaCancelRequest {
    send_email_notification: boolean;
    email_attachment: IEmailAttachment[];
    email_additional_content?: string;
    cancel_reason: string;
}

export interface IRestockRequest {
    selected_product_case_ids: number[];
    restocking_location_id?: string;
}

export interface IRmaAddNoteRequest {
    note: string;
}

export interface IValidationImageUpload{
    filename: string;
    product_case_validation_id: number;
    type: string;
    base64FileData: string;
}

export interface IValidateItemsActionRequest {
    images_to_upload: IValidationImageUpload[],
    images_to_delete: IValidateItemImage[],
    is_returned: boolean,
    returned_comment: string,
    is_restockable: boolean,
    restocked_comment: string,
    product_case_id: number
}

export interface IEmailAttachment {
    name: string;
    type: string;
    base64: string;
}

export interface IRmaData {
	rma: IRma;
	settings: IRmaSettings;
}

export interface ITimelineItemDto {
    type: TIMELINE_ITEM_TYPE_ENUM;
    content: string;
    return_error: IRmaError | null;
    created_at: string;
    is_system: boolean;
    is_automation: boolean;
    user_name: string;
    transaction_data?: ITransactionDataDto;
}

export interface IClassifiedError {
    category: string;
    code: string;
    created_at: string;
    id: number;
    is_generic?: boolean;
    is_category_exclusive: boolean;
    message: string;
    recommended_action: string;
    subcategory: string;
    type: string;
    updated_at: string;
    weight: number;
}

export interface ITransactionDataDto {
    platform: string;
    status: RMA_EXTERNAL_TRANSACTION_STATUS;
    transaction_id: number;
}

export enum TIMELINE_ITEM_TYPE_ENUM {
    TYPE_CREDITED = 'credited',
    TYPE_REFUNDED = 'refunded',
    TYPE_ERROR = 'error',
    TYPE_NOTE = 'note',
    TYPE_EMAIL = 'email',
}

export enum SHIPMENT_STATUS_ENUM {
	PENDING_SHIPMENT = 0,
	LABEL_CREATED = 10,
	SHIPPED = 2,
	RECEIVED = 3,
    REJECTED = 4,
    VERIFIED = 5,
    OUT_FOR_DELIVERY = 6,
    ERROR = 7
};

export const SHIPMENT_STATUS_TRANSLATION_KEYS_MAP = {
	[SHIPMENT_STATUS_ENUM.PENDING_SHIPMENT]: 'WAITING_STATUS',
	[SHIPMENT_STATUS_ENUM.LABEL_CREATED]: 'LABEL_CREATED',
	[SHIPMENT_STATUS_ENUM.SHIPPED]: 'SHIPMENT_SHIPPED_STATUS',
    [SHIPMENT_STATUS_ENUM.RECEIVED]: 'SHIPMENT_DELIVERED_STATUS',
    [SHIPMENT_STATUS_ENUM.REJECTED]: 'SHIPMENT_REJECTED_STATUS',
    [SHIPMENT_STATUS_ENUM.OUT_FOR_DELIVERY]: 'SHIPMENT_OUT_FOR_DELIVERY_STATUS',
	[SHIPMENT_STATUS_ENUM.ERROR]: 'SHIPMENT_STATUS_ERROR',
    [SHIPMENT_STATUS_ENUM.VERIFIED]: 'SHIPMENT_VERIVIED_STATUS'
} as const;

export enum RMA_STATUS {
    PENDING_APPROVAL = 0,
    APPROVED = 1,
    REJECTED = 2,
    DONE = 3,
    RECEIVED = 4,
    VALIDATED = 5,
    CANCELED = 6,
}

export enum DRAFT_ORDER_TYPE {
    DRAFT_ORDER_TYPE_FEE = 'DRAFT_ORDER_TYPE_FEE',
    DRAFT_ORDER_TYPE_INSTANT_EXCHANGE_CAPTURE = 'DRAFT_ORDER_TYPE_INSTANT_EXCHANGE_CAPTURE',
    DRAFT_ORDER_TYPE_EXCHANGE = 'DRAFT_ORDER_TYPE_EXCHANGE',
    DRAFT_ORDER_TYPE_INSTANT_EXCHANGE = 'DRAFT_ORDER_TYPE_INSTANT_EXCHANGE',
    DRAFT_ORDER_TYPE_EXCHANGE_VIA_PORTAL_CHECKOUT = 'DRAFT_ORDER_TYPE_EXCHANGE_VIA_PORTAL_CHECKOUT',
    DRAFT_ORDER_TYPE_FEE_VIA_PORTAL_CHECKOUT = 'DRAFT_ORDER_TYPE_FEE_VIA_PORTAL_CHECKOUT',
}

export const DRAFT_ORDER_EXCHANGE_TYPES = [
    DRAFT_ORDER_TYPE.DRAFT_ORDER_TYPE_EXCHANGE,
    DRAFT_ORDER_TYPE.DRAFT_ORDER_TYPE_INSTANT_EXCHANGE,
    DRAFT_ORDER_TYPE.DRAFT_ORDER_TYPE_EXCHANGE_VIA_PORTAL_CHECKOUT
];
export const DRAFT_ORDER_FEE_TYPES = [
    DRAFT_ORDER_TYPE.DRAFT_ORDER_TYPE_FEE,
    DRAFT_ORDER_TYPE.DRAFT_ORDER_TYPE_FEE_VIA_PORTAL_CHECKOUT,
    DRAFT_ORDER_TYPE.DRAFT_ORDER_TYPE_INSTANT_EXCHANGE_CAPTURE,
];

export enum DRAFT_ORDER_STATE {
    DRAFT_ORDER_CREATED = 'DRAFT_ORDER_CREATED',
    PENDING_INVOICE = 'PENDING_INVOICE',
    PENDING_PAYMENT = 'PENDING_PAYMENT',
    INVOICE_PAID = 'INVOICE_PAID',
}

export enum PAYMENT_STATUS {
    AUTHORIZED = 'requires_capture',
    CANCELED = 'canceled',
    CHARGED = 'succeeded',
};

export enum PRODUCT_CASE_OFFER_STATUS {
    DECLINED = 0,
    OFFERED = 1,
    ACCEPTED = 2,
}

export enum MANAGERS_APPROVAL_REASONS {
    OUT_OF_POLICY = 'out of policy',
    ORDER_OUT_OF_POLICY = 'Order out of policy',
};

export enum CREATE_RMA_SUBMIT_BLOCK_REASONS {
    CUSTOMER_PAYMENT_REQUIRED = 'customer payment required',
    OUT_OF_POLICY_NO_APPROVE_PERMISSION = 'out of policy no approve permission',
};

export interface IGetTranslationData {
    scope: string,
    key: string
};

export interface IManagersApprovalTranslations {
    noteKey?: IGetTranslationData,
    tooltipKey?: IGetTranslationData,
    indicatorKey?: IGetTranslationData
}

export function getManagersApprovalReasonTranslationKeys(reason: MANAGERS_APPROVAL_REASONS): IManagersApprovalTranslations | undefined {
    switch (reason) {
        case MANAGERS_APPROVAL_REASONS.OUT_OF_POLICY: {
            return {
                noteKey: {
                    scope: 'dashboard-create-rma',
                    key: 'THIS_RMA_IS_OUT_OF_POLICY_NOTE_TEXT'
                },
                tooltipKey: {
                    scope: 'dashboard-create-rma',
                    key: 'OOP_NOT_HAVE_APPROVE_PERMISSION_TOOLTIP_TEXT'
                },
                indicatorKey: {
                    scope: 'dashboard-returns-page',
                    key: 'RMA_OUT_OF_POLICY_INDICATOR_TEXT'
                }
            }
        }
        /* This distinction is to fetch relevant keys for either order/rma oop */
        case MANAGERS_APPROVAL_REASONS.ORDER_OUT_OF_POLICY: {
            return {
                indicatorKey: {
                    scope: 'dashboard-create-rma',
                    key: 'ORDER_OUT_OF_POLICY_INDICATOR_TEXT'
                },
            }
        }
    }

    return undefined;
};

export function getCreateRmaSubmitBlockTranslationKeys(
    reason: CREATE_RMA_SUBMIT_BLOCK_REASONS,
): IManagersApprovalTranslations | undefined {
    switch (reason) {
        case CREATE_RMA_SUBMIT_BLOCK_REASONS.CUSTOMER_PAYMENT_REQUIRED:
            return {
                tooltipKey: {
                    scope: 'dashboard-create-rma',
                    key: 'BLOCK_PAID_RMA_TOOLTIP',
                },
            };
        case CREATE_RMA_SUBMIT_BLOCK_REASONS.OUT_OF_POLICY_NO_APPROVE_PERMISSION:
            return getManagersApprovalReasonTranslationKeys(MANAGERS_APPROVAL_REASONS.OUT_OF_POLICY);
    }

    return undefined;
};

export enum RMA_EXTERNAL_TRANSACTION_STATUS {
    PLATFORM_ORDER_TRANSACTION_STATUS_AWAITING_RESPONSE = 'AWAITING_RESPONSE',
    PLATFORM_ORDER_TRANSACTION_STATUS_ERROR = 'ERROR',
    PLATFORM_ORDER_TRANSACTION_STATUS_PENDING = 'PENDING',
    PLATFORM_ORDER_TRANSACTION_STATUS_FAILURE = 'FAILURE',
    PLATFORM_ORDER_TRANSACTION_STATUS_SUCCESS = 'SUCCESS',
    PLATFORM_ORDER_TRANSACTION_STATUS_UNKNOWN = 'UNKNOWN',
}
